import React from "react";
import { Link } from "react-router-dom";
import { FaCompass } from "react-icons/fa";
import {
  useForYouFeed,
  useInteractionTracker,
} from "../../hooks/useRecommendations";
import getProductImageUrl, { resolveProductImagePath } from "../../utils/imageUtils";
import FeedMatchFusedLine from "../feed/FeedMatchFusedLine";
import styles from "../../styles/components/discover/ProductForYouSection.module.css";

const ProductForYouSection = ({ limit = 8, title = "Picked for you", variant = "default" }) => {
  const { feed, loading, error, meta } = useForYouFeed({ limit, autoFetch: true });
  const { trackClick } = useInteractionTracker();
  const interestLabels = (meta?.interestTypes || [])
    .map((interest) => interest?.label)
    .filter(Boolean)
    .slice(0, 2);
  const sectionClassName =
    variant === "hero"
      ? `${styles.section} ${styles.sectionHero}`
      : styles.section;

  if (loading) {
    return (
      <section className={sectionClassName} aria-busy="true">
        <h3 className={styles.title}>{title}</h3>
        <p className={styles.hint}>Loading personalized picks…</p>
      </section>
    );
  }

  if (error || !feed?.length) {
    return null;
  }

  return (
    <section className={sectionClassName} aria-label={title}>
      <header className={styles.header}>
        <FaCompass className={styles.icon} aria-hidden />
        <div>
          <h3 className={styles.title}>{title}</h3>
          <p className={styles.subtitle}>
            {interestLabels.length
              ? `Inspired by your interest in ${interestLabels.join(" and ")}`
              : "Personalized from products you explore"}
          </p>
        </div>
      </header>
      <ul className={styles.grid}>
        {feed.slice(0, limit).map((item) => {
          const product = item.product;
          const id = product?._id || product?.id;
          if (!id) return null;
          return (
            <li key={id} className={styles.card}>
              <Link
                to={`/products/${id}`}
                className={styles.cardLink}
                onClick={() => trackClick(id, "for_you")}
              >
                <div className={styles.imageWrap}>
                  <img
                    src={getProductImageUrl(resolveProductImagePath(product))}
                    alt=""
                    className={styles.image}
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = getProductImageUrl();
                    }}
                  />
                </div>
                <p className={styles.name}>{product?.name}</p>
                <FeedMatchFusedLine
                  product={product}
                  className={styles.fusedLine}
                  precomputedScore={item.displayScore}
                />
                <p className={styles.reason}>
                  {item.reason || "Recommended for you"}
                </p>
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default ProductForYouSection;
